import React, { useState } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { extractPropsFromScriptCloud, extractSoundCuesFromScriptCloud } from '../../sync/ai-api';
import { Button } from '../../../components/common';
import { showToast, confirmDialog } from '../../../utils/notifications';
import { PermissionGuard } from '../../auth/components/PermissionGuard';
import { Permission } from '../../auth/types';
import { logger } from '@/utils/logger';

function useScopedExtractionScope() {
    const cloud = useAppStore(state => state.cloud);
    const enabled = Boolean(cloud.enabled && cloud.orgId && cloud.projectId);

    return {
        scope: enabled ? { orgId: cloud.orgId!, projectId: cloud.projectId! } : null,
    };
}

/**
 * Button to manually extract props from the latest imported source PDF.
 * Appears in the ToolActionBar when viewing the Props tool.
 */
export const ExtractPropsButton: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { scope } = useScopedExtractionScope();
    
    const handleExtract = async () => {
        if (!scope) {
            showToast('Cloud project is required to extract props.', 'warning');
            return;
        }
        
        const confirmed = await confirmDialog(
            'Extract props from the current imported script? This saves results to the Props tab and will not modify script blocks, scenes, acts, or characters.'
        );
        if (!confirmed) return;
        
        setIsLoading(true);
        try {
            showToast('Analyzing script for props...', 'info');
            const result = await extractPropsFromScriptCloud(scope);
            showToast(`Extracted and saved ${result.persisted} props from script.`, 'success');
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            logger.error('Props extraction failed:', error);
            showToast(`Failed to extract props: ${msg}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <PermissionGuard permission={Permission.EDIT_PROPS}>
            <Button
                variant="ghost"
                size="sm"
                leftIcon="auto_awesome"
                onClick={() => { void handleExtract(); }}
                disabled={isLoading || !scope}
                title="Extract props from imported script"
            >
                {isLoading ? 'Extracting...' : 'Extract from Script'}
            </Button>
        </PermissionGuard>
    );
};

/**
 * Button to manually extract sound cues from the latest imported source PDF.
 * Appears in the ToolActionBar when viewing the Sound tool.
 */
export const ExtractSoundCuesButton: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { scope } = useScopedExtractionScope();
    
    const handleExtract = async () => {
        if (!scope) {
            showToast('Cloud project is required to extract sound cues.', 'warning');
            return;
        }
        
        const confirmed = await confirmDialog(
            'Extract sound cues from the current imported script? This saves results to the Sound tab and will not modify script blocks, scenes, acts, or characters.'
        );
        if (!confirmed) return;
        
        setIsLoading(true);
        try {
            showToast('Analyzing script for sound cues...', 'info');
            const result = await extractSoundCuesFromScriptCloud(scope);
            showToast(`Extracted and saved ${result.persisted} sound cues from script.`, 'success');
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            logger.error('Sound cues extraction failed:', error);
            showToast(`Failed to extract sound cues: ${msg}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <PermissionGuard permission={Permission.EDIT_SOUND}>
            <Button
                variant="ghost"
                size="sm"
                leftIcon="auto_awesome"
                onClick={() => { void handleExtract(); }}
                disabled={isLoading || !scope}
                title="Extract sound cues from imported script"
            >
                {isLoading ? 'Extracting...' : 'Extract from Script'}
            </Button>
        </PermissionGuard>
    );
};
